
/**
 * Module dependencies.
 */

import {
  DisclosureName,
  Disclosures,
  FiltersDisclosuresContext
} from './context';

import React, { ReactNode, useCallback, useMemo, useReducer } from 'react';
import get from 'lodash/get';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode
};

/**
 * `State` type.
 */

type State = {
  disclosures: Disclosures
};

/**
 * Action types.
 */

const actionTypes = {
  closeDisclosure: 'CLOSE_DISCLOSURE',
  closeDisclosures: 'CLOSE_DISCLOSURES',
  openDisclosure: 'OPEN_DISCLOSURE',
  openDisclosureAndCloseOthers: 'OPEN_DISCLOSURE_AND_CLOSE_OTHERS'
};

/**
 * Inital state.
 */

const initialState = (disclosures: Disclosures) => Object
  .keys(disclosures)
  .reduce((result: Disclosures, key: string): Disclosures => ({
    ...result,
    [key]: false
  }), {});

/**
 * Reducer.
 */

function reducer(state: State, { payload, type }) {
  switch (type) {
    case actionTypes.closeDisclosure: {
      return {
        disclosures: {
          ...state.disclosures,
          [payload.name]: false
        }
      };
    }

    case actionTypes.closeDisclosures: {
      return {
        disclosures: {
          ...state.disclosures,
          ...initialState(state.disclosures)
        }
      };
    }

    case actionTypes.openDisclosure: {
      return {
        disclosures: {
          ...state.disclosures,
          [payload.name]: true
        }
      };
    }

    case actionTypes.openDisclosureAndCloseOthers: {
      return {
        disclosures: {
          ...state.disclosures,
          ...initialState(state.disclosures),
          [payload.name]: true
        }
      };
    }

    default:
      return state;
  }
}

/**
 * `FiltersDisclosuresProvider` provider.
 */

const FiltersDisclosuresProvider = ({ children }: Props) => {
  const [{ disclosures }, dispatch] = useReducer(reducer, {
    disclosures: {}
  });

  const handleClose = useCallback((name: string) => {
    dispatch({
      payload: { name },
      type: actionTypes.closeDisclosure
    });
  }, []);

  const handleCloseAll = useCallback(() => {
    dispatch({
      payload: null,
      type: actionTypes.closeDisclosures
    });
  }, []);

  const handleOpen = useCallback((name: DisclosureName) => {
    dispatch({
      payload: { name },
      type: actionTypes.openDisclosure
    });
  }, []);

  const handleOpenAndCloseOthers = useCallback((name: DisclosureName) => {
    dispatch({
      payload: { name },
      type: actionTypes.openDisclosureAndCloseOthers
    });
  }, []);

  const handleToggle = useCallback((name: DisclosureName) => {
    const isOpen = get(disclosures, name) === true;

    if (isOpen) {
      handleClose(name);

      return;
    }

    handleOpen(name);
  }, [disclosures, handleClose, handleOpen]);

  const getDisclosureState = useCallback((name: DisclosureName) => {
    return get(disclosures, name);
  }, [disclosures]);

  const value = useMemo(() => ({
    disclosures,
    getDisclosureState,
    onClose: handleClose,
    onCloseAll: handleCloseAll,
    onOpen: handleOpen,
    onOpenAndCloseOthers: handleOpenAndCloseOthers,
    onToggle: handleToggle
  }), [
    disclosures,
    getDisclosureState,
    handleClose,
    handleCloseAll,
    handleOpen,
    handleOpenAndCloseOthers,
    handleToggle
  ]);

  return (
    <FiltersDisclosuresContext.Provider value={value}>
      {children}
    </FiltersDisclosuresContext.Provider>
  );
};

/**
 * Export `FiltersDisclosuresProvider` provider.
 */

export default FiltersDisclosuresProvider;
